import { clientChartNotesOptions, type Pagination as PaginationMeta } from '@cartwheel/api/v1'
import {
  DataTable,
  EmptyState,
  FilterBar,
  LoadingView,
  Pagination,
  TextInput,
} from '@cartwheel/components'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { type ChartNote, ChartNoteRow } from './chart-note-row'

interface Props {
  clientId: string
}

type SortKey = 'date' | 'noteType' | 'clinician' | 'score'
type SortDirection = 'asc' | 'desc'

const DEFAULT_PER_PAGE = 10

export default function ClientChartNotesList({ clientId }: Props) {
  const [sortKey, setSortKey] = useState<SortKey>('date')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE)

  const { data, isLoading, isFetching, error } = useQuery({
    ...clientChartNotesOptions({
      path: { client_id: clientId },
      query: { page: String(page), per_page: String(perPage) },
    }),
    placeholderData: keepPreviousData,
  })

  const chartNotes: ChartNote[] = (data?.chart_notes as ChartNote[] | undefined) ?? []

  const paginationMeta: PaginationMeta | undefined = data?.pagination

  const filteredNotes = useMemo(() => {
    if (!search.trim()) return chartNotes
    const q = search.toLowerCase()
    return chartNotes.filter(
      (n) => n.note_type?.toLowerCase().includes(q) || n.clinician_name?.toLowerCase().includes(q)
    )
  }, [chartNotes, search])

  const sortedNotes = useMemo(() => {
    const getValue = (note: ChartNote, key: SortKey): string | number => {
      switch (key) {
        case 'date':
          return note.date ? Date.parse(note.date) : 0
        case 'noteType':
          return note.note_type || ''
        case 'clinician':
          return note.clinician_name || ''
        case 'score':
          return note.latest_clinical_quality_score?.percentage ?? -1
      }
    }

    const sorted = [...filteredNotes].sort((a, b) => {
      const aVal = getValue(a, sortKey)
      const bVal = getValue(b, sortKey)
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return aVal - bVal
      }
      return String(aVal).localeCompare(String(bVal))
    })

    return sortDirection === 'asc' ? sorted : sorted.reverse()
  }, [filteredNotes, sortKey, sortDirection])

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDirection('asc')
    }
  }

  const handleSearch = (value: string) => {
    setSearch(value)
    setPage(1)
  }

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
  }

  const handlePerPageChange = (newPerPage: number) => {
    setPerPage(newPerPage)
    setPage(1)
  }

  const totalCount: number = paginationMeta?.total_count ?? chartNotes.length

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingView title="Loading chart notes..." />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-orange">Error loading chart notes</div>
      </div>
    )
  }

  return (
    <div className="px-4 py-8 lg:px-8 flex-1 overflow-auto">
      <FilterBar
        rightSlot={
          <div className="relative">
            <i className="fa-solid fa-search absolute left-3 top-1/2 -translate-y-1/2 text-charcoal-50 text-sm pointer-events-none" />
            <TextInput
              placeholder="Search by note type or clinician"
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-9 w-72"
            />
          </div>
        }
      >
        <span className="text-sm text-charcoal-50">
          {totalCount} chart note{totalCount !== 1 ? 's' : ''}
        </span>
      </FilterBar>

      {chartNotes.length === 0 ? (
        <EmptyState
          title="No chart notes found"
          description="Chart notes will appear here once sessions have been documented in Healthie."
        />
      ) : sortedNotes.length === 0 ? (
        <EmptyState title="No results" description={`No chart notes match "${search}".`} />
      ) : (
        <>
          <div
            className={`w-full border border-gray-stroke rounded-lg bg-white overflow-hidden transition-opacity duration-150 ${isFetching ? 'opacity-50' : ''}`}
          >
            <DataTable.Root className="min-w-full divide-y divide-gray-stroke">
              <DataTable.Header>
                <DataTable.Row>
                  <DataTable.HeaderCell
                    sortable
                    sortDirection={sortKey === 'date' ? sortDirection : null}
                    onSort={() => handleSort('date')}
                  >
                    Date
                  </DataTable.HeaderCell>
                  <DataTable.HeaderCell
                    sortable
                    sortDirection={sortKey === 'noteType' ? sortDirection : null}
                    onSort={() => handleSort('noteType')}
                  >
                    Note Type
                  </DataTable.HeaderCell>
                  <DataTable.HeaderCell
                    sortable
                    sortDirection={sortKey === 'clinician' ? sortDirection : null}
                    onSort={() => handleSort('clinician')}
                  >
                    Clinician
                  </DataTable.HeaderCell>
                  <DataTable.HeaderCell
                    sortable
                    sortDirection={sortKey === 'score' ? sortDirection : null}
                    onSort={() => handleSort('score')}
                  >
                    Quality Score
                  </DataTable.HeaderCell>
                  <DataTable.HeaderCell />
                  <DataTable.HeaderCell />
                </DataTable.Row>
              </DataTable.Header>
              <DataTable.Body className="bg-white divide-y divide-gray-stroke">
                {sortedNotes.map((note) => (
                  <ChartNoteRow key={note.id} chartNote={note} clientId={clientId} />
                ))}
              </DataTable.Body>
            </DataTable.Root>
          </div>

          {paginationMeta && (
            <div className="mt-4">
              <Pagination
                currentPage={page}
                itemsPerPage={perPage}
                totalPages={paginationMeta.total_pages}
                totalCount={paginationMeta.total_count}
                from={paginationMeta.from}
                to={paginationMeta.to}
                hasNextPage={paginationMeta.has_next_page}
                hasPrevPage={paginationMeta.has_prev_page}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handlePerPageChange}
                itemsPerPageOptions={[10, 25, 50, 100]}
                disabled={isFetching}
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}
